/**
 * @module FCEvents
 * @description Custom event system for the FitCheck theme. Defines event-name constants
 * (FCEvents) and typed Event subclasses for variant picks, cart actions, filter changes,
 * carousel picks, and other cross-component communication.
 */
// Theme event name constants
export class FCEvents {
  static variantPick = 'variant:pick';
  static variantChange = 'variant:change';
  static sellingPlanChange = 'selling-plan:change';
  static cartRefresh = 'cart:refresh';
  static cartSectionsRendered = 'cart:sections-rendered';
  static cartFail = 'cart:fail';
  static cartAddPending = 'cart:add-pending';
  static cartAddPendingClear = 'cart:add-pending-clear';
  static cartCountSync = 'cart:count-sync';
  static mediaPlay = 'media:play';
  static qtyChange = 'qty:change';
  static megaMenuEnter = 'mega-menu:enter';
  static zoomPick = 'zoom:pick';
  static discountChange = 'discount:change';
  static filterChange = 'filter:change';
  static carouselPick = 'carousel:pick';
}

// Helpers
const safeNum = (val) => {
  const parsed = typeof val === 'number' ? val : Number.parseInt(String(val ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};
const safeObject = (val) => (val && typeof val === 'object' && !Array.isArray(val) ? val : null);
const safeString = (val) => (val == null || val === '' ? null : String(val));
const fallbackEventTarget = () =>
  typeof document !== 'undefined' && typeof document.dispatchEvent === 'function' ? document : null;

const CART_ADD_SOURCES = new Set(['product-form', 'quick-add']);
const CART_COUNT_SYNC_PHASES = new Set(['start', 'finish']);
let cartAddOperationSequence = 0;

export const CartEventStatus = Object.freeze({
  pending: 'pending',
  success: 'success',
  error: 'error',
});

export const CartEventAction = Object.freeze({
  add: 'add',
  refresh: 'refresh',
});

function normalizeCartAction(action) {
  return action === CartEventAction.add || action === CartEventAction.refresh ? action : null;
}

function safeNullableNum(value) {
  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function dispatchReachableEvent(target, event) {
  const fallbackTarget = fallbackEventTarget();
  const canDispatchFromTarget =
    target &&
    typeof target.dispatchEvent === 'function' &&
    (!('isConnected' in target) || target.isConnected !== false);

  (canDispatchFromTarget ? target : fallbackTarget)?.dispatchEvent?.(event);
}

function normalizeCartEventData(data, defaults = {}) {
  const raw = safeObject(data) ?? {};
  const didError = defaults.didError ?? (raw.didError === true || raw.status === CartEventStatus.error);
  const isPending = raw.status === CartEventStatus.pending || defaults.status === CartEventStatus.pending;

  return {
    ...raw,
    status: didError ? CartEventStatus.error : isPending ? CartEventStatus.pending : CartEventStatus.success,
    didError,
    action: normalizeCartAction(raw.action) ?? defaults.action ?? null,
    source: safeString(raw.source),
    itemCount: safeNum(raw.itemCount),
    productId: safeString(raw.productId),
    variantId: safeString(raw.variantId),
    sections: safeObject(raw.sections),
  };
}

export function createCartAddOperationId(source = 'cart-add') {
  cartAddOperationSequence += 1;
  return `${safeString(source) || 'cart-add'}-${Date.now()}-${cartAddOperationSequence}`;
}

export function getCartEventData(event) {
  return normalizeCartEventData(event?.detail?.data);
}

export function getCartCountSyncData(event) {
  const raw = safeObject(event?.detail?.data) ?? {};
  const phase = CART_COUNT_SYNC_PHASES.has(raw.phase) ? raw.phase : 'finish';

  return {
    phase,
    operationId: safeString(raw.operationId),
    count: safeNullableNum(raw.count),
    delta: safeNullableNum(raw.delta) ?? 0,
    source: safeString(raw.source),
    didError: raw.didError === true,
  };
}

export function isCartAddSuccessEvent(event) {
  const data = getCartEventData(event);
  const isAddAction = data.action === CartEventAction.add || CART_ADD_SOURCES.has(data.source || '');
  return isAddAction && data.didError === false && data.status === CartEventStatus.success;
}

export function isCartAddPendingEvent(event) {
  const data = getCartEventData(event);
  const isAddAction = data.action === CartEventAction.add || CART_ADD_SOURCES.has(data.source || '');
  return event?.type === CartAddPendingEvent.eventName && isAddAction && data.status === CartEventStatus.pending;
}

export function emitCartAddPending(target, sourceId, data = {}) {
  dispatchReachableEvent(target, new CartAddPendingEvent(sourceId, data));
}

export function emitCartAddPendingClear(target, sourceId, data = {}) {
  dispatchReachableEvent(target, new CartAddPendingClearEvent(sourceId, data));
}

export function emitCartAddSuccess(target, sourceId, resource, data) {
  dispatchReachableEvent(target, new CartAddEvent(resource, sourceId, data));
}

export function emitCartAddFailure(target, sourceId, failure, data = {}) {
  dispatchReachableEvent(
    target,
    new CartFailEvent(sourceId, failure?.message, failure?.description, failure?.errors, data),
  );
}

export function emitCartCountSync(target, data = {}) {
  dispatchReachableEvent(target, new CartCountSyncEvent(data));
}

export class VariantPickEvent extends Event {
  constructor(resource) {
    super(FCEvents.variantPick, { bubbles: true });
    this.detail = { resource: resource || null };
  }
}

export class VariantChangeEvent extends Event {
  constructor(resource, sourceId, data) {
    super(FCEvents.variantChange, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId: sourceId || null,
      data: {
        html: data?.html || null,
        productId: data?.productId || null,
        newProduct: data?.newProduct || null,
      },
    };
  }
}

export class CartAddEvent extends Event {
  static eventName = FCEvents.cartRefresh;

  constructor(resource, sourceId, data) {
    super(CartAddEvent.eventName, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId: sourceId || null,
      data: normalizeCartEventData(data, { action: CartEventAction.add }),
    };
  }
}

export class CartRefreshEvent extends Event {
  constructor(resource, sourceId, data) {
    super(FCEvents.cartRefresh, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId: sourceId || null,
      data: normalizeCartEventData(data, { action: CartEventAction.refresh }),
    };
  }
}

export class CartAddPendingEvent extends Event {
  static eventName = FCEvents.cartAddPending;

  constructor(sourceId, data) {
    super(CartAddPendingEvent.eventName, { bubbles: true });
    this.detail = {
      sourceId: sourceId || null,
      data: normalizeCartEventData(data, {
        action: CartEventAction.add,
        status: CartEventStatus.pending,
      }),
    };
  }
}

export class CartAddPendingClearEvent extends Event {
  static eventName = FCEvents.cartAddPendingClear;

  constructor(sourceId, data) {
    super(CartAddPendingClearEvent.eventName, { bubbles: true });
    this.detail = {
      sourceId: sourceId || null,
      data: normalizeCartEventData(data, { action: CartEventAction.add }),
    };
  }
}

export class CartSectionsRenderedEvent extends Event {
  static eventName = FCEvents.cartSectionsRendered;

  constructor(resource, sourceId, data, options = {}) {
    super(CartSectionsRenderedEvent.eventName, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId: sourceId || null,
      sectionId: safeString(options.sectionId),
      data: normalizeCartEventData(data, { action: CartEventAction.refresh }),
    };
  }
}

export class CartFailEvent extends Event {
  constructor(sourceId, message, description, errors, data) {
    super(FCEvents.cartFail, { bubbles: true });
    const normalized = normalizeCartEventData(
      {
        ...(safeObject(data) ?? {}),
        message: message || null,
        errors: errors || null,
        description: description || null,
      },
      { action: CartEventAction.add, didError: true },
    );

    this.detail = {
      sourceId: sourceId || null,
      data: normalized,
    };
  }
}

export class CartCountSyncEvent extends Event {
  static eventName = FCEvents.cartCountSync;

  constructor(data) {
    super(CartCountSyncEvent.eventName, { bubbles: true });
    this.detail = {
      data: getCartCountSyncData({ detail: { data } }),
    };
  }
}

export class QtyChangeEvent extends Event {
  constructor(quantity, cartLine, cartKey = null) {
    super(FCEvents.qtyChange, { bubbles: true });
    this.detail = { quantity: safeNum(quantity), cartLine: cartLine || null, cartKey: safeString(cartKey) };
  }
}

export class DiscountChangeEvent extends Event {
  constructor(resource, sourceId, data) {
    super(FCEvents.discountChange, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId: sourceId || null,
      data: normalizeCartEventData(data, { action: CartEventAction.refresh }),
    };
  }
}

export class MediaPlayEvent extends Event {
  constructor(resource) {
    super(FCEvents.mediaPlay, { bubbles: true });
    this.detail = { resource: resource || null };
  }
}

export class CarouselPickEvent extends Event {
  static eventName = FCEvents.carouselPick;
  detail;

  constructor(data) {
    super(CarouselPickEvent.eventName, { bubbles: true });
    this.detail = data || {};
  }
}

export class ZoomPickEvent extends Event {
  constructor(index) {
    super(FCEvents.zoomPick, { bubbles: true });
    this.detail = { index: safeNum(index) };
  }
}

export class MegaMenuEnterEvent extends Event {
  constructor() {
    super(FCEvents.megaMenuEnter, { bubbles: true });
  }
}

export class FilterChangeEvent extends Event {
  constructor(queryParams) {
    super(FCEvents.filterChange, { bubbles: true });
    this.detail = { queryParams: queryParams || new URLSearchParams() };
  }

  shouldShowClearAll() {
    const params = this.detail?.queryParams;
    if (!params || typeof params.entries !== 'function') return false;
    return [...params.entries()].some(([key]) => typeof key === 'string' && key.startsWith('filter.'));
  }
}
